import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import content from "../variants.json";

const CreatorCashContext = createContext();
export const useCreatorCashContext = () => useContext(CreatorCashContext);

// Helper functions
const EXPERIMENT_ID = "exp1_creatorcash_lp";
const VARIANT_TAG = "creatorcash_variant";
const VARIANT_TTL_DAYS = 30;

const saveVariant = (name, value, days) => {
    localStorage.setItem(name, value);
};

const getVariant = (name) => {
    return localStorage.getItem(name);
};

const randomVariant = () => {
    const variants = ["a", "b", "c"];
    const index = Math.floor(Math.random() * variants.length);
    return variants[index];
};

const CreatorCashProvider = ({ children }) => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const [variant, setVariant] = useState(null);
    const [theme, setTheme] = useState();
    const [authState, setAuthState] = useState(sessionStorage.getItem("oAuthState"));

    const detectedErrorType = () => {
        const queryValue = new URLSearchParams(search);
        if ([queryValue.get("error"), queryValue.get("reject"), queryValue.get("status") === "success" ? "true" : "false"].every((element) => element === "true")) {
            return "reject_subscribers_under_1000"
        } else if ([queryValue.get("oauth_error"), queryValue.get("error")].every((element) => element === "true")) {
            return "oauth_error"
        } else if ([queryValue.get("other_error"), queryValue.get("error")].every((element) => element === "true")) {
            return "other_error"
        } else if ([queryValue.get("error"), queryValue.get("reject")].every((element) => element === "true")) {
            return "reject"
        } else if (queryValue.get("code") === "nochannel") {
            return "nochannel"
        } else {
            return "error"
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const errorType = useCallback(() => detectedErrorType(), []);

    useEffect(() => {
        const queryValue = new URLSearchParams(search);
        const variantParam = queryValue.get("v");
        const successState = [queryValue.get("success"), queryValue.get("status") === "success" ? "true" : "false"].includes("true");
        const errorState = [queryValue.get("code") === "nochannel" ? "true" : "false", queryValue.get("error"), queryValue.get("reject"), queryValue.get("oauth_error"), queryValue.get("other_error")].includes("true") ? true : false;
        const rejectionState = [queryValue.get("error"), queryValue.get("reject"), queryValue.get("status") === "success" ? "true" : "false"].every((element) => element === "true");
        const storedVariant = getVariant(VARIANT_TAG);
        const oAuthState = sessionStorage.getItem("oAuthState");

        // --- Handle Variant Logic ---
        if (variantParam) {
            saveVariant(VARIANT_TAG, variantParam, VARIANT_TTL_DAYS);
            setVariant(variantParam);
        } else if (storedVariant) {
            setVariant(storedVariant);
            const cleanUrl = `${window.location.pathname}${storedVariant ? "?v=" + storedVariant: ""}`;
            window.history.replaceState({}, "", cleanUrl);
        } else {
            // Randomly assign new visitor
            const newVariant = randomVariant();
            saveVariant(VARIANT_TAG, newVariant, VARIANT_TTL_DAYS);
            setVariant(newVariant);
            const cleanUrl = `${window.location.pathname}${newVariant ? "?v=" + newVariant: ""}`;
            window.history.replaceState({}, "", cleanUrl);
        }

        // --- Handle OAuth States ---
        if (rejectionState) {
            if (oAuthState !== "submitted") {
                sessionStorage.setItem("oAuthState", "rejected");
                setAuthState("rejected");
                if (window.gtag) {
                    window.gtag('event', 'connection_failed', {
                        variant: variant?.toUpperCase(),
                    });
                    window.gtag('event', 'connect_youtube_error', {
                        error_type: errorType(),
                        variant: variant?.toUpperCase(),
                    });
                }
            }
        } else if (errorState) {
            if (oAuthState !== "submitted") {
                sessionStorage.setItem("oAuthState", "failed");
                setAuthState("failed");
                if (window.gtag) {
                    window.gtag('event', 'connection_failed', {
                        variant: variant?.toUpperCase(),
                    });
                    window.gtag('event', 'connect_youtube_error', {
                        error_type: errorType(),
                        variant: variant?.toUpperCase(),
                    });
                }
            }
        } else if (successState) {
            if (window.fbq) {
                window.fbq('track', 'Lead');
            }
            if (window.ttq) {
                window.ttq?.track('Lead', {})
            }
            if (window.gtag) {
                window.gtag('event', 'connection_success', {
                    variant: variant?.toUpperCase(),
                });
            }
            const prevState = sessionStorage.getItem("oAuthState");
            if (prevState !== "failed") {
                sessionStorage.setItem("oAuthState", "submitted");
                setAuthState("submitted");
            }
        } else {
            if (oAuthState !== "submitted") {
                sessionStorage.setItem("oAuthState", "general");
                setAuthState("general");
            }
        }

    }, [search, navigate, errorType, variant]);

    // --- Set theme based on variant ---
    useEffect(() => {
        if (variant && content[variant]) {
            setTheme(content[variant]);

            // Send experiment data to GA4
            if (window.gtag) {
                window.gtag("event", "experiment_view", {
                    experiment_id: EXPERIMENT_ID,
                    variant: variant?.toUpperCase(),
                });
            }
        }
    }, [variant]);

    return (
        <CreatorCashContext.Provider
            value={{
                state: {
                    authState,
                    variant,
                    welcomeSection: theme?.sections?.welcomebanner,
                    creatorsSection: theme?.sections?.creators,
                    creatorCashSection: theme?.sections?.creatorcash,
                    requirementsToJoin: theme?.sections?.requirementsToJoin,
                    compareYourOptions: theme?.sections?.compareYourOptions,
                    howItWorks: theme?.sections?.howItWorks,
                    faq: theme?.sections?.faq,
                },
                actions: {
                    setAuthState,
                },
            }}
        >
            {children}
        </CreatorCashContext.Provider>
    );
};

export default CreatorCashProvider;
